import type { BookRaw, Manuscript } from './types';
import type { Tier } from '@/state/GameState';
import { MANUS_MIX_PER_TIER, TIER_ORDEN } from '@/systems/tier';
import { anvendOverlay, lesOverlay } from './bokOverlay';

const VALID_TIERS: ReadonlySet<Tier> = new Set<Tier>(['D', 'C', 'B', 'A', 'S']);

export type UbruktValg = {
  sjanger: string;
  ekskluder: ReadonlySet<string>;
  rng?: () => number;
};

export class BookCatalog {
  private manuscripts: Manuscript[] = [];
  private originalRaw: BookRaw[] = [];
  private loaded = false;

  loadFromArray(raw: BookRaw[]): void {
    this.originalRaw = raw.map((b) => ({ ...b }));
    const overlay = lesOverlay();
    const merged = anvendOverlay(this.originalRaw, overlay);
    this.manuscripts = merged
      .map((b) => this.normalise(b))
      .filter((b): b is Manuscript => b !== null);
    this.loaded = true;
  }

  reapplyOverlay(): void {
    if (this.originalRaw.length === 0) return;
    const overlay = lesOverlay();
    const merged = anvendOverlay(this.originalRaw, overlay);
    this.manuscripts = merged
      .map((b) => this.normalise(b))
      .filter((b): b is Manuscript => b !== null);
  }

  getOriginalRaw(): ReadonlyArray<BookRaw> {
    return this.originalRaw;
  }

  getAll(): ReadonlyArray<Manuscript> {
    return this.manuscripts;
  }

  async loadFromUrl(url: string): Promise<void> {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Klarte ikke laste ${url}: ${res.status}`);
    const data = (await res.json()) as BookRaw[];
    this.loadFromArray(data);
  }

  get size(): number {
    return this.manuscripts.length;
  }

  get isReady(): boolean {
    return this.loaded;
  }

  randomManuscripts(
    count: number,
    exclude: ReadonlySet<string>,
    seed?: number,
    spillerTier: Tier = 'D',
  ): Manuscript[] {
    const pool = this.manuscripts.filter((m) => !exclude.has(m.isbn));
    if (pool.length === 0) return [];

    const rng = seed === undefined ? Math.random : mulberry32(seed);
    const perTier: Record<Tier, Manuscript[]> = {
      D: [],
      C: [],
      B: [],
      A: [],
      S: [],
    };
    for (const m of pool) perTier[m.tier].push(m);

    const mix = MANUS_MIX_PER_TIER[spillerTier];
    const result: Manuscript[] = [];
    const valgte = new Set<string>();

    const onsketAntall = Math.min(count, pool.length);

    let forsoek = 0;
    const maksForsoek = count * 40;
    while (result.length < onsketAntall && forsoek < maksForsoek) {
      forsoek += 1;
      const tilgjengelig = TIER_ORDEN.filter((t) =>
        perTier[t].some((m) => !valgte.has(m.isbn)),
      );
      if (tilgjengelig.length === 0) break;

      const onsketTier = vektedTierValg(mix, rng);
      const valgtTier =
        onsketTier && tilgjengelig.includes(onsketTier)
          ? onsketTier
          : finnNaermesteTilgjengelige(onsketTier ?? 'D', tilgjengelig, rng);
      if (!valgtTier) break;

      const ledige = perTier[valgtTier].filter((m) => !valgte.has(m.isbn));
      if (ledige.length === 0) continue;
      const kandidat = ledige[Math.floor(rng() * ledige.length)];
      valgte.add(kandidat.isbn);
      result.push(kandidat);
    }
    return result;
  }

  findByIsbn(isbn: string): Manuscript | null {
    return this.manuscripts.find((m) => m.isbn === isbn) ?? null;
  }

  findUbruktMedSjanger(
    sjanger: string,
    ekskluder: ReadonlySet<string>,
    rng: () => number = Math.random,
  ): Manuscript | null {
    if (!this.loaded || this.manuscripts.length === 0) return null;
    const matchende = this.manuscripts.filter(
      (m) => m.genre === sjanger && !ekskluder.has(m.isbn),
    );
    if (matchende.length > 0) {
      const idx = Math.floor(rng() * matchende.length);
      return matchende[Math.min(matchende.length - 1, idx)];
    }
    const enhver = this.manuscripts.filter((m) => !ekskluder.has(m.isbn));
    if (enhver.length === 0) return null;
    const idx = Math.floor(rng() * enhver.length);
    return enhver[Math.min(enhver.length - 1, idx)];
  }

  private normalise(raw: BookRaw): Manuscript | null {
    const isbn = (raw.isbn ?? raw.id ?? '').trim();
    const title = (raw.title ?? '').trim();
    const author = (raw.author ?? '').trim() || 'Ukjent forfatter';
    const blurb = (raw.editorial_blurb ?? '').trim();
    if (!isbn || !title || !blurb) return null;

    const tierRaw = (raw.tier ?? 'D').toUpperCase();
    const tier: Tier = VALID_TIERS.has(tierRaw as Tier) ? (tierRaw as Tier) : 'D';

    return {
      isbn,
      title,
      author,
      genre: raw.genre ?? 'LITERARY',
      tier,
      editorial_blurb: blurb,
      cover_url: raw.cover_url ?? '',
    };
  }
}

function vektedTierValg(
  mix: Record<Tier, number>,
  rng: () => number,
): Tier | null {
  const r = rng();
  let kum = 0;
  for (const t of TIER_ORDEN) {
    kum += mix[t];
    if (r < kum) return t;
  }
  return null;
}

function finnNaermesteTilgjengelige(
  onsket: Tier,
  tilgjengelig: ReadonlyArray<Tier>,
  rng: () => number,
): Tier | null {
  if (tilgjengelig.length === 0) return null;
  const onsketIdx = TIER_ORDEN.indexOf(onsket);
  for (let avstand = 1; avstand < TIER_ORDEN.length; avstand += 1) {
    const kandidater: Tier[] = [];
    const ned = onsketIdx - avstand;
    const opp = onsketIdx + avstand;
    if (ned >= 0 && tilgjengelig.includes(TIER_ORDEN[ned])) {
      kandidater.push(TIER_ORDEN[ned]);
    }
    if (opp < TIER_ORDEN.length && tilgjengelig.includes(TIER_ORDEN[opp])) {
      kandidater.push(TIER_ORDEN[opp]);
    }
    if (kandidater.length > 0) {
      return kandidater[Math.floor(rng() * kandidater.length)];
    }
  }
  return tilgjengelig[0];
}

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function rng(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const bookCatalog = new BookCatalog();
